import { useCallback, useEffect, useRef, useState } from 'react'
import { getAudioLosslessUrl, resolveMediaUrl, type AudioLosslessStatus, type AudioLosslessStorageBackend } from '../../api'

export type AudioSourceQuality = 'mp3' | 'flac'

export type PlayableTrack = {
  id: string
  title: string
  artist: string | null
  file_url: string | null
  duration_seconds: number | null
  lossless_status?: AudioLosslessStatus
  lossless_object_key?: string | null
  lossless_storage_backend?: AudioLosslessStorageBackend | null
}

export type AudioPlayerState = {
  playingTrack: PlayableTrack | null
  activeAudioSource: AudioSourceQuality | null
  preferLossless: boolean
  audioLoading: boolean
  audioPlaying: boolean
  audioCurrentTime: number
  audioDuration: number
  audioMuted: boolean
  audioVolume: number
}

export type AudioPlayerControls = {
  playTrack: (track: PlayableTrack, options?: { restart?: boolean }) => void
  togglePlayback: () => void
  setLosslessPreference: (enabled: boolean) => void
  stopPlayback: () => void
  seekPlayback: (time: number) => void
  updateAudioVolume: (volume: number) => void
  toggleAudioMute: () => void
}

type AudioPlayerOptions = {
  onEnded?: (() => void) | undefined
  onError?: (() => void) | undefined
}

const getAdminAudioPreviewUrl = (fileUrl: string): string => {
  const resolvedUrl = resolveMediaUrl(fileUrl)
  const separator = resolvedUrl.includes('?') ? '&' : '?'

  return `${resolvedUrl}${separator}preview=${Date.now()}`
}

const getTrackAudioSource = (
  track: PlayableTrack,
  preferLossless: boolean
): { quality: AudioSourceQuality; url: string } | null => {
  const mp3Url = track.file_url?.trim() ? track.file_url : null
  const flacUrl = getAudioLosslessUrl({
    lossless_object_key: track.lossless_object_key ?? null,
    lossless_storage_backend: track.lossless_storage_backend ?? null,
    lossless_status: track.lossless_status ?? 'missing'
  })

  if (preferLossless && flacUrl) {
    return { quality: 'flac', url: flacUrl }
  }

  if (mp3Url) {
    return { quality: 'mp3', url: mp3Url }
  }

  if (flacUrl) {
    return { quality: 'flac', url: flacUrl }
  }

  return null
}

export const useAudioPlayer = (options: AudioPlayerOptions = {}): AudioPlayerState & AudioPlayerControls => {
  const [playingTrack, setPlayingTrack] = useState<PlayableTrack | null>(null)
  const [activeAudioSource, setActiveAudioSource] = useState<AudioSourceQuality | null>(null)
  const [preferLossless, setPreferLossless] = useState(true)
  const [audioLoading, setAudioLoading] = useState(false)
  const [audioPlaying, setAudioPlaying] = useState(false)
  const [audioCurrentTime, setAudioCurrentTime] = useState(0)
  const [audioDuration, setAudioDuration] = useState(0)
  const [audioMuted, setAudioMuted] = useState(false)
  const [audioVolume, setAudioVolume] = useState(1)
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  const playTokenRef = useRef(0)
  const pendingSeekRef = useRef<number | null>(null)
  const onEndedRef = useRef(options.onEnded)
  const onErrorRef = useRef(options.onError)

  onEndedRef.current = options.onEnded
  onErrorRef.current = options.onError

  useEffect(() => {
    const audio = audioElementRef.current

    if (audio) {
      audio.volume = audioVolume
      audio.muted = audioMuted
    }
  }, [audioMuted, audioVolume])

  const stopPlayback = useCallback((): void => {
    const audio = audioElementRef.current

    playTokenRef.current += 1
    pendingSeekRef.current = null
    if (audio) {
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
    }
    setPlayingTrack(null)
    setActiveAudioSource(null)
    setAudioLoading(false)
    setAudioPlaying(false)
    setAudioCurrentTime(0)
    setAudioDuration(0)
  }, [])

  const startAudioTrack = useCallback(async (track: PlayableTrack, preferFlac = preferLossless, offset = 0): Promise<void> => {
    const token = playTokenRef.current + 1
    const source = getTrackAudioSource(track, preferFlac)
    const audio = audioElementRef.current

    playTokenRef.current = token
    pendingSeekRef.current = offset > 0 ? offset : null
    setPlayingTrack(track)
    setAudioLoading(!!source)
    setAudioPlaying(false)
    setAudioCurrentTime(offset)
    setAudioDuration(track.duration_seconds ?? 0)
    setActiveAudioSource(source?.quality ?? null)

    if (!source || !audio) {
      setAudioLoading(false)
      return
    }

    try {
      audio.pause()
      audio.src = getAdminAudioPreviewUrl(source.url)
      audio.load()
      await audio.play()

      if (playTokenRef.current !== token) {
        audio.pause()
      }
    } catch {
      if (playTokenRef.current === token) {
        setAudioLoading(false)
        setAudioPlaying(false)
      }
    }
  }, [preferLossless])

  const togglePlayback = useCallback((): void => {
    const audio = audioElementRef.current

    if (!audio || !playingTrack || !audio.src) {
      return
    }

    if (audioPlaying) {
      audio.pause()
      setAudioPlaying(false)
      return
    }

    setAudioLoading(true)
    void audio.play().catch(() => {
      setAudioLoading(false)
      setAudioPlaying(false)
    })
  }, [audioPlaying, playingTrack])

  const seekPlayback = useCallback((time: number): void => {
    const audio = audioElementRef.current

    if (!audio || !playingTrack || !audio.src) {
      return
    }

    const duration = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : audioDuration
    const nextTime = duration > 0 ? Math.max(0, Math.min(time, duration)) : Math.max(0, time)

    pendingSeekRef.current = nextTime
    try {
      audio.currentTime = nextTime
      pendingSeekRef.current = null
    } catch {
      // Some browsers reject seeking until metadata is available.
    }
    setAudioCurrentTime(nextTime)
  }, [audioDuration, playingTrack])

  const updateAudioVolume = useCallback((volume: number): void => {
    const nextVolume = Math.max(0, Math.min(1, volume))

    setAudioVolume(nextVolume)
    setAudioMuted(nextVolume === 0)
  }, [])

  const toggleAudioMute = useCallback((): void => {
    setAudioMuted((currentMuted) => !currentMuted)
  }, [])

  const setLosslessPreference = useCallback((enabled: boolean): void => {
    setPreferLossless(enabled)

    if (!playingTrack) {
      return
    }

    const currentSource = getTrackAudioSource(playingTrack, preferLossless)
    const nextSource = getTrackAudioSource(playingTrack, enabled)

    if (!nextSource || (
      currentSource?.quality === nextSource.quality
      && currentSource.url === nextSource.url
    )) {
      return
    }

    void startAudioTrack(playingTrack, enabled, audioElementRef.current?.currentTime ?? audioCurrentTime)
  }, [audioCurrentTime, playingTrack, preferLossless, startAudioTrack])

  useEffect(() => {
    const audio = new Audio()
    audio.preload = 'auto'
    audio.volume = audioVolume
    audio.muted = audioMuted
    audioElementRef.current = audio

    const applyPendingSeek = (): void => {
      const pendingSeek = pendingSeekRef.current

      if (pendingSeek === null) {
        return
      }

      try {
        audio.currentTime = pendingSeek
        pendingSeekRef.current = null
      } catch {
        // Keep the pending seek until the browser allows seeking this media.
      }
    }

    const updateDuration = (): void => {
      if (Number.isFinite(audio.duration)) {
        setAudioDuration(audio.duration)
      }
      applyPendingSeek()
    }

    const handleTimeUpdate = (): void => {
      setAudioCurrentTime(audio.currentTime)
    }

    const handleWaiting = (): void => {
      if (!audio.paused && !audio.ended) {
        setAudioLoading(true)
      }
    }

    const handlePlaying = (): void => {
      setAudioLoading(false)
      setAudioPlaying(true)
    }

    const handlePause = (): void => {
      setAudioPlaying(false)
      if (!audio.ended) {
        setAudioLoading(false)
      }
    }

    const handleEnded = (): void => {
      pendingSeekRef.current = null
      setPlayingTrack(null)
      setActiveAudioSource(null)
      setAudioLoading(false)
      setAudioPlaying(false)
      setAudioCurrentTime(0)
      onEndedRef.current?.()
    }

    const handleError = (): void => {
      setAudioLoading(false)
      setAudioPlaying(false)
      onErrorRef.current?.()
    }

    audio.addEventListener('loadedmetadata', updateDuration)
    audio.addEventListener('durationchange', updateDuration)
    audio.addEventListener('canplay', updateDuration)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('waiting', handleWaiting)
    audio.addEventListener('stalled', handleWaiting)
    audio.addEventListener('playing', handlePlaying)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)

    return () => {
      playTokenRef.current += 1
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
      audio.removeEventListener('loadedmetadata', updateDuration)
      audio.removeEventListener('durationchange', updateDuration)
      audio.removeEventListener('canplay', updateDuration)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('waiting', handleWaiting)
      audio.removeEventListener('stalled', handleWaiting)
      audio.removeEventListener('playing', handlePlaying)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
      audioElementRef.current = null
    }
  }, [])

  const playTrack = useCallback((track: PlayableTrack, options?: { restart?: boolean }): void => {
    if (playingTrack?.id === track.id && !options?.restart) {
      togglePlayback()
      return
    }

    void startAudioTrack(track)
  }, [playingTrack, startAudioTrack, togglePlayback])

  return {
    playingTrack,
    activeAudioSource,
    preferLossless,
    audioLoading,
    audioPlaying,
    audioCurrentTime,
    audioDuration,
    audioMuted,
    audioVolume,
    playTrack,
    togglePlayback,
    setLosslessPreference,
    stopPlayback,
    seekPlayback,
    updateAudioVolume,
    toggleAudioMute
  }
}
